/**
 * Featured (custom) segment card — rendered inside the "Personnalisés" tab.
 *
 * Shows segment name, description, client count, average revenue, and a
 * delete button that asks for confirmation before firing useDeleteSegment.
 *
 * Extracted from Segments.tsx during the Maintainability Sprint 2026-04-19
 * (Task 1.1 step 1.1.9 — final squeeze). Behavior and French copy are
 * preserved byte-for-byte. Coverage in ../__tests__/Segments.test.tsx
 * ("renders featured segment cards when data is present").
 */

import { Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { formatFCFA } from "@cieda/utils";
import { useDeleteSegment } from "@/hooks/admin/useCRM";
import type { FeaturedSegmentRow } from "@/lib/admin-api/crm";

interface FeaturedSegmentCardProps {
  segment: FeaturedSegmentRow;
}

export default function FeaturedSegmentCard({
  segment,
}: FeaturedSegmentCardProps) {
  const deleteMutation = useDeleteSegment();

  const handleDelete = () => {
    if (!window.confirm(`Supprimer le segment "${segment.name}" ?`)) return;
    deleteMutation.mutate(segment.id, {
      onSuccess: () => toast.success("Segment supprimé"),
      onError: () => toast.error("Erreur lors de la suppression"),
    });
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-stone-100">
          <Users className="h-5 w-5 text-stone-600" />
        </div>
        <button
          onClick={handleDelete}
          className="text-stone-400 hover:text-red-500 transition-colors"
          title="Supprimer"
          aria-label={`Supprimer ${segment.name}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3">
        <span className="inline-flex items-center rounded-full bg-stone-100 px-2.5 py-0.5 text-xs font-semibold text-stone-700">
          {segment.name}
        </span>
        {segment.description && (
          <p className="mt-1.5 text-sm text-stone-600">{segment.description}</p>
        )}
      </div>
      <div className="mt-4 flex items-end justify-between border-t border-black/5 pt-3">
        <div>
          <p className="text-xs text-stone-500">Clients</p>
          <p className="text-xl font-display font-semibold text-stone-800">
            {segment.customer_count.toLocaleString("fr-SN")}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-stone-500">Revenu moyen</p>
          <p className="text-sm font-semibold text-stone-800">
            {formatFCFA(segment.avg_revenue_fcfa)}
          </p>
        </div>
      </div>
    </div>
  );
}
